import { ApolloError, useApolloClient, useMutation, useQuery } from "@apollo/client";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MergeIcon from "@mui/icons-material/Merge";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { prettyPrintWasteClassification } from "../../prettyPrintWasteClassification";
import { useWasteFacilityState } from "../../WasteFacilityContext";
import { computeMergeContainerImpact } from "./mergeContainerImpact";
import { MergedDestinationsDataGrid } from "./MergedDestinationsDataGrid";
import { MergedWasteStreamInfo } from "./MergedWasteStreamInfo";
import { MergedWasteStreamInternalNotesInput } from "./MergedWasteStreamInternalNotesInput";
import { MergedWasteStreamNameInput } from "./MergedWasteStreamNameInput";
import { MergedWasteStreamsDataGrid } from "./MergedWasteStreamsDataGrid";
import {
  defaultRegistrationSourceId,
  getRegistrationCandidates,
  needsRegistrationChoice,
} from "./registrationSource";
import { RegistrationSourcePicker } from "./RegistrationSourcePicker";
import { buildMergeInput, WasteStreamMergeFormData } from "./wasteStreamMergeInput";

const MERGE_WASTE_STREAMS = gql(`
  mutation MergeWasteStreams($input: MergeWasteStreamsInput!) {
    mergeWasteStreams(input: $input)
  }
`);

const MERGE_WASTE_STREAM_CONTAINERS = gql(`
  query MergeWasteStreamContainers($wasteStreamIds: [ID!]!) {
    wasteStreamContainers(wasteStreamIds: $wasteStreamIds) {
      id
      containerLabel
      wasteStream {
        id
        name
        classification
      }
    }
  }
`);

interface WasteStreamMergeDialogProps {
  onClose: () => void;
  selectedRows: any[]; // Replace with proper type when available
}

export const WasteStreamMergeDialog = ({ onClose, selectedRows }: WasteStreamMergeDialogProps) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const client = useApolloClient();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const {
    wasteFacilityState: { deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const [mergeWasteStreams, { loading: merging }] = useMutation(MERGE_WASTE_STREAMS);

  // Container impact is only surfaced when the tenant has container tracking
  // enabled; the query itself is flag-gated server-side, so it must be skipped
  // when the flag is off (otherwise it would be forbidden).
  const { featureFlags } = useFeatureFlags();
  const containerTrackingEnabled = !!featureFlags[FeatureFlagKey.ContainerTracking];

  const selectedIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);
  const { data: containerData } = useQuery(MERGE_WASTE_STREAM_CONTAINERS, {
    variables: { wasteStreamIds: selectedIds },
    skip: !containerTrackingEnabled || selectedIds.length === 0,
  });

  const containerImpact = useMemo(
    () => computeMergeContainerImpact(selectedRows, containerData?.wasteStreamContainers ?? []),
    [selectedRows, containerData]
  );
  // The streams grid only lets staff co-select same-classification streams, so
  // in the normal flow every selected stream shares a classification and this
  // never fires. It stands as the safety net for the merge mutation, which has
  // no server-side classification guard: repointing a container onto a
  // differently classified stream changes its compliance math, so require an
  // explicit acknowledgment rather than letting such a merge pass silently.
  const [acknowledgedMismatch, setAcknowledgedMismatch] = useState(false);
  const mergeBlockedOnMismatch = containerImpact.mismatches.length > 0 && !acknowledgedMismatch;

  const wasteStreamNames = useMemo(() => selectedRows.map((row) => row.name), [selectedRows]);

  const registrationCandidates = useMemo(
    () => getRegistrationCandidates(selectedRows),
    [selectedRows]
  );
  const showRegistrationPicker = useMemo(
    () => needsRegistrationChoice(selectedRows),
    [selectedRows]
  );

  const methods = useForm<WasteStreamMergeFormData>({
    defaultValues: {
      comment:
        selectedRows
          .map((row) => row.comment?.trim())
          .filter(Boolean)
          .join("; ") || "",
      internalNote: "",
      wasteStreamName: selectedRows[0]?.name || "",
      wasteStreamIds: selectedRows.map((row) => row.id),
      stateRegistrationSourceWasteStreamId: showRegistrationPicker
        ? defaultRegistrationSourceId(selectedRows)
        : undefined,
    },
  });

  const { control, handleSubmit } = methods;

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else {
      // Step 2: Submit the form
      handleSubmit(async (data) => {
        try {
          const result = await mergeWasteStreams({
            variables: {
              input: buildMergeInput(data, showRegistrationPicker),
            },
          });

          if (result.errors) {
            alerts.error("Failed to merge waste streams", result.errors[0].message);
          } else if (result.data?.mergeWasteStreams) {
            alerts.success("Waste streams merged successfully");
            // Refetch only the WasteStreams query for the current facility,
            // not stale instances from previously visited facilities (which
            // may belong to other tenants and would 403).
            await client.refetchQueries({
              include: ["WasteStreams"],
              onQueryUpdated(observableQuery) {
                if (observableQuery.variables?.facilityId === effectiveWasteFacilityId) {
                  return observableQuery.refetch();
                }
                // Skip refetch for other facilities
                return false;
              },
            });
          }

          onClose(); // Always close and clear selections
        } catch (error) {
          const message =
            error instanceof ApolloError
              ? error.graphQLErrors.map((e) => e.message).join("; ") || error.message
              : error instanceof Error
                ? error.message
                : "An unexpected error occurred";
          alerts.error("Failed to merge waste streams", message);
          onClose(); // Close even on error
        }
      })();
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const getTitle = () => {
    if (currentStep === 1) {
      return `Merge ${selectedRows.length} waste stream${selectedRows.length === 1 ? "" : "s"}`;
    }
    return "Review merged waste streams";
  };

  const dateRangeText = useMemo(() => {
    const startDate =
      deferredFilters.startDate && getUtcDisplayDate(deferredFilters.startDate, "short");
    const endDate = deferredFilters.endDate && getUtcDisplayDate(deferredFilters.endDate, "short");
    if (!startDate || !endDate) return "";
    return `${startDate}-${endDate}`;
  }, [deferredFilters.startDate, deferredFilters.endDate]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={true}
        onClose={onClose}
        title={getTitle()}
        maxWidth="lg"
        fullWidth
        noPrimaryAction={true}
        secondaryActionElements={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
        ]}
        additionalPrimaryActionElements={[
          <Typography
            key="step-indicator"
            variant="body2"
            sx={{
              color: "text.secondary",
              mr: 2,
            }}
          >
            Step {currentStep}/2
          </Typography>,
          <Button
            key="next"
            variant="contained"
            onClick={handleNext}
            disabled={merging || (currentStep === 2 && mergeBlockedOnMismatch)}
            startIcon={currentStep === 2 ? <MergeIcon /> : undefined}
            endIcon={currentStep === 1 ? <ChevronRightIcon /> : undefined}
            sx={{
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.warning.light,
            }}
          >
            {currentStep === 1 ? "Review Merge" : merging ? "Merging..." : "Merge"}
          </Button>,
        ]}
      >
        <Box>
          {/* Back button shown only on step 2 */}
          {currentStep === 2 && (
            <Button
              startIcon={<ChevronLeftIcon />}
              onClick={handleBack}
              sx={{
                mb: 1,
                mt: theme.spacing(-1.5), // Negative margin to reduce spacing from header
                justifyContent: "flex-start",
              }}
            >
              Back
            </Button>
          )}

          {/* Helper text shown on both steps */}
          <Typography sx={{ mb: 3, color: "text.secondary" }}>
            This merge affects these waste streams for <strong>all time</strong>, not just the
            selected time range ({dateRangeText})
          </Typography>

          {/* Step-specific content */}
          {currentStep === 1 && (
            <MergedWasteStreamsDataGrid wasteStreams={selectedRows} dateRangeText={dateRangeText} />
          )}

          {currentStep === 2 && (
            <Box>
              <Grid container spacing={3}>
                {/* First row - editable field */}
                <Grid size={{ xs: 12 }}>
                  <MergedWasteStreamNameInput
                    control={control}
                    wasteStreamNames={wasteStreamNames}
                  />
                </Grid>
              </Grid>

              {/* Read-only waste stream info */}
              <Box sx={{ mt: 3 }}>
                <MergedWasteStreamInfo wasteStreams={selectedRows} dateRangeText={dateRangeText} />
              </Box>
              {showRegistrationPicker && (
                <Box sx={{ mt: 3 }}>
                  <RegistrationSourcePicker control={control} candidates={registrationCandidates} />
                </Box>
              )}

              {containerTrackingEnabled && containerImpact.totalCount > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity={containerImpact.mismatches.length > 0 ? "warning" : "info"}>
                    <AlertTitle>
                      This merged stream will contain {containerImpact.totalCount} container
                      {containerImpact.totalCount === 1 ? "" : "s"}
                    </AlertTitle>
                    <Box component="ul" sx={{ m: 0, pl: 3 }}>
                      {containerImpact.containers.map((container) => (
                        <li key={container.id}>
                          <Typography variant="body2">
                            <strong>{container.label}</strong> — from {container.originalStreamName}{" "}
                            {container.repointed ? "(repointed)" : "(stays)"}
                            {container.classificationChanges &&
                              ` — classification changes to ${prettyPrintWasteClassification(
                                selectedRows[0]?.classification
                              )}`}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                    {containerImpact.mismatches.length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Repointing changes the classification of{" "}
                          {containerImpact.mismatches.length} container
                          {containerImpact.mismatches.length === 1 ? "" : "s"}, which affects
                          accumulation totals and thresholds.
                        </Typography>
                        <FormControlLabel
                          sx={{ mt: 1 }}
                          control={
                            <Checkbox
                              checked={acknowledgedMismatch}
                              onChange={(e) => setAcknowledgedMismatch(e.target.checked)}
                            />
                          }
                          label="I understand these containers' classification will change"
                        />
                      </>
                    )}
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {/* Internal Note field shown on both steps */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamInternalNotesInput control={control} />
          </Box>

          {/* Merged Destinations Data Grid - Only on Step 2 */}
          {currentStep === 2 && (
            <MergedDestinationsDataGrid wasteStreamIds={selectedRows.map((row) => row.id)} />
          )}
        </Box>
      </Dialog>
    </FormProvider>
  );
};
